// Need to use the React-specific entry point to import createApi
import { BaseQueryError, createApi } from '@reduxjs/toolkit/query/react'
import { decamelizeKeys } from 'humps'

import { DEFAULT_PAGE_LIMIT, FETCH_ALL_PAGE_SIZE } from 'app/lib/constants'
import { asyncPoolAll } from 'app/lib/legacy-api/helpers'
import customBaseQuery from 'app/lib/utils/customBaseQuery'
import { AddAdminArgs, AddAdminResponse, DeleteAdminArgs } from 'app/models/Admin'
import { Collection, CursorPaginated, Item, Paginated } from 'app/models/api'
import {
  ActionRequest,
  AddActionRequestArgs,
  GetEligibilityRecordsParams,
  GetEligibilityRecordsParamsLegacy,
  SubmitEligibilityDirectiveArgs,
  SubmitEligibilityDirectiveResponse,
} from 'app/models/EligibilityRecord'
import {
  CreateEligibilityTagRequest,
  EligibilityTag,
  UpdateEligibilityTagRequest,
} from 'app/models/EligibilityTag'
import {
  AddAdultFamilyMemberArg,
  AddChildFamilyMemberArg,
  AddExistingFamilyMemberArg,
  FamilyMember,
  RemoveFamilyMemberArg,
} from 'app/models/FamilyMembers'
import {
  AddOrganizationAddressArgs,
  AddOrganizationArgs,
  AddPlanArgs,
  fromV1ListOrganization,
  fromV1Organization,
  GetEligiblePeriodsParams,
  GetFeaturesResponse,
  GetOrganizationPlansParams,
  GetOrganizationResponse,
  GetPlanParams,
  ListOrganizationResponse,
  ListOrganizationsParams,
  CreateOrganizationResourceArgs,
  DeleteOrganizationResourceArgs,
  OrganizationEngagementResource,
  UpdateOrganizationResourceArgs,
  UpdateOrganizationAddressArgs,
  UpdateOrganizationArgs,
  UpdateOrganizationContentTagsArgs,
  UpdateOrganizationPlanArgs,
  UpdatePlanArgs,
} from 'app/models/Organization'
import {
  Address,
  AdminArea,
  Administrator,
  Brands,
  Cobrand,
  ContentTag,
  EligibilityRecord,
  EligiblePeriod,
  Feature,
  Organization,
  OrganizationListItem,
  OrganizationV2,
  Plan,
  PlanWithServices,
  ResendInvitationRequestParams,
  UserCharges,
  UserEligibilityRecord,
} from 'app/models/scribe.models'
import {
  EligibilityRecordsArg,
  EnrolEligibilityRecordArg,
  User,
  GetUsersParams,
} from 'app/models/User'

// Define a service using a base URL and expected endpoints
export const scribeApi = createApi({
  reducerPath: 'scribeV2Api',
  tagTypes: [
    'EligibilityRecord',
    'Feature',
    'Organization',
    'OrganizationPlans',
    'OrganizationResource',
    'UserFamily',
    'UserCharges',
    'User',
    'Administrators',
    'AdminArea',
    'EligibilityTag',
  ],
  baseQuery: customBaseQuery,
  refetchOnMountOrArgChange: process.env.VITE_APP_ENV === 'test',
  invalidationBehavior: 'immediately',
  endpoints: (builder) => ({
    listOrganizations: builder.query<Paginated<Organization>, ListOrganizationsParams>({
      query: (params) => ({
        url: '/v1/organizations',
        params: {
          limit: params.limit || DEFAULT_PAGE_LIMIT,
          offset: params.offset || 0,
          is_brand_organization: params.is_brand_organization,
          ...(params.search ? { search: params.search } : {}),
        },
      }),
      transformResponse: (response: ListOrganizationResponse) => fromV1ListOrganization(response),
      providesTags: () => [{ type: 'Organization', id: 'LIST' }],
    }),
    // FIXME: remove legacy endpoint at some point.
    legacyGetOrganization: builder.query<Organization, string>({
      query: (organizationId) => ({
        url: `/v1/organizations/${organizationId}`,
        params: { include: 'default_credit_card,addresses' },
      }),
      transformResponse: (response: GetOrganizationResponse) => fromV1Organization(response),
      providesTags: (_result, _error, organizationId) => [
        { type: 'Organization', id: organizationId },
      ],
    }),
    getOrganization: builder.query<OrganizationV2, string>({
      query: (organizationId) => ({
        url: `/v2/organizations/${organizationId}`,
      }),
      transformResponse: (response: Item<OrganizationV2>) => response.data,
      providesTags: (_result, _error, organizationId) => [
        { type: 'Organization', id: organizationId },
      ],
    }),
    getAllOrganizations: builder.query<OrganizationListItem[], void>({
      queryFn: async (_args, _api, _options, baseQueryFetch) => {
        const fetchPage = async (cursor: string | null) =>
          baseQueryFetch({
            url: '/v3/organizations',
            params: { limit: FETCH_ALL_PAGE_SIZE, cursor },
          })

        let organizations: OrganizationListItem[] = []
        let cursor: string | null = null

        do {
          const response = await fetchPage(cursor)
          if (response.error) return { error: response.error }

          const page = response.data as CursorPaginated<OrganizationListItem>
          organizations = organizations.concat(page.data)
          cursor = page.meta.nextCursor
        } while (cursor)

        return { data: organizations }
      },
      providesTags: () => [{ type: 'Organization', id: 'LIST' }],
    }),
    addOrganization: builder.mutation<Organization, AddOrganizationArgs>({
      query: ({ body }) => ({
        url: `/v1/organizations`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: Item<Organization>) => response.data,
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'organizationPage.successfulQuery.organization',
        errorCodes: 'organizationPage.errorQuery.organization',
      },
    }),
    updateOrganization: builder.mutation<Organization, UpdateOrganizationArgs>({
      query: ({ organizationId, body }) => ({
        url: `/v1/organizations/${organizationId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'Organization', id: organizationId },
        { type: 'Organization', id: 'LIST' },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'organizationPage.successfulQuery.organizationEdit',
        errorCodes: 'organizationPage.errorQuery.organizationEdit',
      },
    }),
    updateOrganizationReportingColumns: builder.mutation<Organization, UpdateOrganizationArgs>({
      query: ({ organizationId, body }) => ({
        url: `/v1/organizations/${organizationId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'Organization', id: organizationId },
      ],
    }),
    addOrganizationAddress: builder.mutation<Address, AddOrganizationAddressArgs>({
      query: ({ organizationId, body }) => ({
        url: `/v1/organizations/${organizationId}/addresses`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: Item<Address>) => response.data,
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'organizationPage.successfulQuery.address',
        errorCodes: 'organizationPage.errorQuery.address',
      },
    }),
    updateOrganizationAddress: builder.mutation<Address, UpdateOrganizationAddressArgs>({
      query: ({ organizationId, addressId, body }) => ({
        url: `/v1/organizations/${organizationId}/addresses/${addressId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'Organization', id: organizationId },
      ],
    }),
    updateOrganizationContentTags: builder.mutation<
      ContentTag[],
      UpdateOrganizationContentTagsArgs
    >({
      query: ({ organizationId, body }) => ({
        url: `/v2/organizations/${organizationId}/content_tags`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Collection<ContentTag>) => response.data,
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'organizationPage.successfulQuery.organizationContentTagEdit',
        errorCodes: 'organizationPage.errorQuery.organizationContentTagEdit',
      },
    }),
    // FIXME: remove legacy endpoint once Services & Options are fully
    // supported by Scribe v2. For now we still rely on the legacy features
    // data model which exist only in Scribe v1.
    LegacyGetOrganizationPlans: builder.query<Plan[], string>({
      query: (organizationId) => `/v1/organizations/${organizationId}/plans`,
      transformResponse: (plans: Collection<Plan>) => plans.data,
      providesTags: (_result, _error, organizationId) => [
        { type: 'OrganizationPlans', id: organizationId },
      ],
    }),
    getOrganizationPlans: builder.query<PlanWithServices[], GetOrganizationPlansParams>({
      query: ({ organizationId, ...params }) => ({
        url: `/v2/organizations/${organizationId}/plans`,
        params: {
          limit: FETCH_ALL_PAGE_SIZE,
          ...params,
        },
      }),
      transformResponse: (plans: Collection<PlanWithServices>) => plans.data,
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationPlans', id: organizationId },
      ],
    }),
    getOrganizationPlan: builder.query<Plan, GetPlanParams>({
      query: ({ organizationId, planId }) => `/v1/organizations/${organizationId}/plans/${planId}`,
      transformResponse: (plans: Item<Plan>) => plans.data,
      providesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationPlans', id: organizationId },
      ],
    }),
    getCoBrands: builder.query<Cobrand[], {}>({
      query: () => `/v1/cobrands`,
      transformResponse: (coBrands: Item<Cobrand[]>) => coBrands.data,
    }),
    getBrands: builder.query<Brands[], {}>({
      query: () => `/v2/brands`,
      transformResponse: (brands: Item<Brands[]>) => brands.data,
    }),
    addOrganizationPlan: builder.mutation<Plan, AddPlanArgs>({
      query: ({ organizationId, body }) => ({
        url: `/v2/organizations/${organizationId}/plans`,
        method: 'POST',
        body,
      }),
      transformResponse: (plans: { data: Plan }) => plans.data,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationPlans', id: organizationId },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'planPage.successfulQuery.added',
        errorCodes: 'planPage.errorQuery.added',
      },
    }),
    updateOrganizationPlan: builder.mutation<Plan, UpdateOrganizationPlanArgs>({
      query: ({ organizationId, planId, body }) => ({
        url: `/v1/organizations/${organizationId}/plans/${planId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (plans: Item<Plan>) => plans.data,
      // Updating a plan must invalidates all plans, because the default plan may have changed
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationPlans', id: organizationId },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'planPage.successfulQuery.updated',
        errorCodes: 'planPage.errorQuery.updated',
      },
    }),
    removeOrganizationPlan: builder.mutation<null, string>({
      query: (planId) => ({
        url: `/v2/plans/${planId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OrganizationPlans'],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'planPage.successfulQuery.deleted',
        errorCodes: 'planPage.errorQuery.deleted',
      },
    }),
    updatePlan: builder.mutation<Item<Plan>, UpdatePlanArgs>({
      query: ({ planId, body }) => ({
        url: `/v2/plans/${planId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['OrganizationPlans'],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'planPage.successfulQuery.updated',
        errorCodes: 'planPage.errorQuery.updated',
      },
    }),
    archiveOrganizationPlan: builder.mutation<Item<Plan>, UpdatePlanArgs>({
      query: ({ planId, body }) => ({
        url: `/v2/plans/${planId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['OrganizationPlans'],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'planPage.successfulQuery.archived',
        errorCodes: 'planPage.errorQuery.archived',
      },
    }),
    getFeatures: builder.query<Feature[], {}>({
      query: () => `/v1/features`,
      transformResponse: (result: GetFeaturesResponse) => result.data.map((x) => x.attributes),
      providesTags: () => [{ type: 'Feature', id: 'LIST' }],
    }),
    getEligibilityRecord: builder.query<EligibilityRecord, string | undefined>({
      query: (eligibilityRecordId) => `/v2/eligibility_records/${eligibilityRecordId}`,
      transformResponse: (eligibilityRecord: Item<EligibilityRecord>) => eligibilityRecord?.data,
      providesTags: (result) => (result ? [{ type: 'EligibilityRecord', id: result.id }] : []),
    }),
    getEligibilityRecords: builder.query<
      CursorPaginated<EligibilityRecord>,
      GetEligibilityRecordsParams
    >({
      query: (params) => ({
        url: `/v3/eligibility_records`,
        params: {
          ...params,
          limit: params.limit || DEFAULT_PAGE_LIMIT,
        },
      }),
    }),
    listEligibilityRecords: builder.query<
      Paginated<EligibilityRecord>,
      GetEligibilityRecordsParamsLegacy
    >({
      query: (getParams) => ({
        url: '/v2/eligibility_records',
        params: {
          ...getParams.params,
          attributes: JSON.stringify(decamelizeKeys(getParams.params?.attributes)),
        },
      }),
      providesTags: () => [{ type: 'EligibilityRecord', id: 'LIST' }],
    }),
    addActionRequest: builder.mutation<Item<ActionRequest>, AddActionRequestArgs>({
      query: ({ eligibilityRecordId, body }) => ({
        url: `/v2/eligibility_records/${eligibilityRecordId}/action_requests`,
        method: 'POST',
        body,
      }),
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'organizationEapRequests.form.successfulQuery',
        errorCodes: 'organizationEapRequests.form.errorQuery',
      },
    }),
    submitEligibilityDirective: builder.mutation<
      SubmitEligibilityDirectiveResponse,
      SubmitEligibilityDirectiveArgs
    >({
      query: ({ organizationId, body, params, extraOptions }) => ({
        url: `/v2/organizations/${organizationId}/eligibility_directive`,
        method: 'POST',
        body,
        params,
        extraOptions,
      }),
      invalidatesTags: (result) => [
        { type: 'EligibilityRecord', id: result?.data?.id },
        { type: 'EligibilityRecord', id: 'LIST' },
        { type: 'Organization', id: result?.data?.organizationId },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'eligibilityRecordPage.success',
        errorCodes: 'eligibilityRecordPage.errorQuery',
      },
    }),
    getUser: builder.query<User, string | undefined>({
      query: (userId) => `/v2/users/${userId}`,
      transformResponse: (admins: Item<User>) => admins.data,
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),
    getUsers: builder.query<CursorPaginated<User>, GetUsersParams>({
      query: (paginationParams) => ({
        url: `/v3/users`,
        params: {
          ...paginationParams,
          limit: DEFAULT_PAGE_LIMIT,
        },
      }),
    }),
    getUserFamily: builder.query<Collection<FamilyMember>, string | undefined>({
      query: (userId) => `/v2/users/${userId}/family_members`,
      providesTags: (_result, _error, userId) => [{ type: 'UserFamily', id: userId }],
    }),
    getUserCharges: builder.query<UserCharges, string | undefined>({
      query: (userId) => `/v1/users/${userId}/charges`,
      providesTags: (_result, _error, userId) => [{ type: 'UserCharges', id: userId }],
    }),
    getUserEligibilityRecords: builder.query<UserEligibilityRecord, EligibilityRecordsArg>({
      query: ({ params, userId }) => ({
        url: `/v2/eligibility_records`,
        method: 'GET',
        params: {
          limit: params.limit,
          offset: params.offset,
          participant_id: userId,
          include_trust_based: params.include_trust_based,
        },
      }),
      providesTags: (_result, _error, { userId }) => [{ type: 'User', id: userId }],
    }),
    addAdultFamilyMember: builder.mutation<Collection<FamilyMember>, AddAdultFamilyMemberArg>({
      query: ({ userId, body }) => ({
        url: `/v2/users/${userId}/family_members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserFamily', id: userId }],
      extraOptions: { showErrorTooltip: true, successTooltip: 'addMember.successfulQuery' },
    }),
    addExistingFamilyMember: builder.mutation<FamilyMember, AddExistingFamilyMemberArg>({
      query: ({ userId, body }) => ({
        url: `/v2/users/${userId}/family_members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserFamily', id: userId }],
      extraOptions: { showErrorTooltip: true, successTooltip: 'addMember.successfulQuery' },
    }),
    addChildFamilyMember: builder.mutation<Item<FamilyMember>, AddChildFamilyMemberArg>({
      query: ({ userId, body }) => ({
        url: `/v1/users/${userId}/children`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserFamily', id: userId }],
      extraOptions: { showErrorTooltip: true, successTooltip: 'addMember.successfulQuery' },
    }),
    removeFamilyMember: builder.mutation<null, RemoveFamilyMemberArg>({
      query: ({ userId, targetUserId }) => ({
        url: `/v2/users/${userId}/family_members/${targetUserId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserFamily', id: userId }],
      extraOptions: { showErrorTooltip: true, successTooltip: 'removeMember.successfulQuery' },
    }),
    addAdmin: builder.mutation<Administrator[], AddAdminArgs>({
      query: ({ organizationId, body }) => ({
        url: `/v2/organizations/${organizationId}/administrators`,
        method: 'POST',
        body,
      }),
      transformResponse: (admins: AddAdminResponse) => admins.data,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'Administrators', id: organizationId },
        { type: 'Organization', id: organizationId },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'adminPage.successfulQuery.added',
        errorCodes: 'adminPage.errorQuery',
      },
    }),
    deleteAdmin: builder.mutation<void, DeleteAdminArgs>({
      query: ({ organizationId, administratorId }) => ({
        url: `/v2/organizations/${organizationId}/administrators/${administratorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'Administrators', id: organizationId },
        { type: 'Organization', id: organizationId },
      ],
      extraOptions: {
        showErrorTooltip: true,
        successTooltip: 'adminPage.successfulQuery.removed',
        errorCodes: 'adminPage.errorQuery',
      },
    }),
    getAllEligiblePeriods: builder.query<EligiblePeriod[], GetEligiblePeriodsParams>({
      queryFn: async (args, _api, _options, baseQueryFetch) => {
        const { planIds, params } = args

        const fetchPlanPeriods = async (planId: string) => {
          const url = `/v3/plans/${planId}/eligible_periods`

          const fetchPage = (cursor?: string | null) =>
            baseQueryFetch({
              url,
              params: {
                ...params,
                limit: FETCH_ALL_PAGE_SIZE,
                cursor,
              },
            })

          let allData: EligiblePeriod[] = []
          let cursor: string | null = null

          do {
            const response = await fetchPage(cursor)
            if (response.error) return { error: response.error }

            const pageData = response.data as CursorPaginated<EligiblePeriod>
            allData = allData.concat(pageData.data)
            cursor = pageData.meta.nextCursor
          } while (cursor)

          return { data: allData.map((period) => ({ ...period, planId })) }
        }

        const allPlansResponses = await asyncPoolAll(5, planIds, fetchPlanPeriods)

        const errorResponse = allPlansResponses.find((response) => response.error)
        if (errorResponse) {
          return errorResponse as BaseQueryError<any>
        }

        const allPlansPeriods = allPlansResponses.reduce<EligiblePeriod[]>(
          (acc, curr) => acc.concat(curr.data as EligiblePeriod[]),
          [] as EligiblePeriod[],
        )

        return { data: allPlansPeriods }
      },
    }),
    getAdminAreas: builder.query<AdminArea[], string>({
      query: (countryCode) => ({
        url: `/v1/countries/${countryCode}/admin_areas`,
      }),
      providesTags: ['AdminArea'],
      transformResponse: (areas: Collection<AdminArea>) => areas.data,
    }),
    enrolUserToEligibilityRecord: builder.mutation<null, EnrolEligibilityRecordArg>({
      query: ({ userId, body }) => ({
        url: `/v2/users/${userId}/enrol_by_eligibility_record`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId, body }) => [
        { type: 'User', id: userId },
        { type: 'EligibilityRecord', id: body.eligibility_record_id },
      ],
    }),
    unEnrolUserToEligibilityRecord: builder.mutation<null, EnrolEligibilityRecordArg>({
      query: ({ userId, body }) => ({
        url: `/v2/users/${userId}/unenrol_by_eligibility_record`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { userId, body }) => [
        { type: 'User', id: userId },
        { type: 'EligibilityRecord', id: body.eligibility_record_id },
      ],
    }),
    sendEmailVerification: builder.mutation<null, string>({
      query: (authId) => ({
        url: `/v2/email_verification`,
        method: 'POST',
        body: {
          auth0_user_id: authId,
        },
      }),
    }),
    resendWelcomeEmail: builder.mutation<null, string>({
      query: (id: string) => ({
        url: `/v2/organizations/${id}/emails/resend_welcome_emails`,
        method: 'POST',
      }),
    }),
    resendMemberInvitationEmail: builder.mutation<null, ResendInvitationRequestParams>({
      query: ({ body }) => ({
        url: '/v2/invitations/resend',
        method: 'POST',
        body,
      }),
    }),

    getEligibilityTags: builder.query<EligibilityTag[], void>({
      query: () => ({
        url: '/v2/eligibility_tags',
      }),
      providesTags: () => [{ type: 'EligibilityTag', id: 'LIST' }],
      transformResponse: (response: Collection<EligibilityTag>) => response.data,
    }),
    addEligibilityTag: builder.mutation({
      query: (body: CreateEligibilityTagRequest) => ({
        url: '/v2/eligibility_tags',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EligibilityTag'],
    }),
    updateEligibilityTag: builder.mutation<null, UpdateEligibilityTagRequest>({
      query: ({ id, body }) => ({
        url: `/v2/eligibility_tags/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: () => [{ type: 'EligibilityTag', id: 'LIST' }],
    }),
    linkEligibilityTagToRecord: builder.mutation<
      null,
      { eligibilityRecordId: number; tagId: number }
    >({
      query: ({ eligibilityRecordId, tagId }) => ({
        url: `/v2/eligibility_tags/${tagId}/eligibility_records`,
        method: 'POST',
        body: {
          eligibility_record_id: eligibilityRecordId,
        },
      }),
      invalidatesTags: ['EligibilityRecord'],
    }),
    unlinkEligibilityTagFromRecord: builder.mutation<
      null,
      { eligibilityRecordId: number; tagId: number }
    >({
      query: ({ eligibilityRecordId, tagId }) => ({
        url: `/v2/eligibility_tags/${tagId}/eligibility_records/${eligibilityRecordId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EligibilityRecord'],
    }),
    getOrganizationResources: builder.query<OrganizationEngagementResource[], string>({
      query: (organizationId) => ({
        url: `/v2/organizations/${organizationId}/resources`,
      }),
      transformResponse: (response: Collection<OrganizationEngagementResource>) => response.data,
      providesTags: (_result, _error, organizationId) => [
        { type: 'OrganizationResource', id: organizationId },
      ],
    }),
    deleteOrganizationResource: builder.mutation<void, DeleteOrganizationResourceArgs>({
      query: ({ organizationId, organizationResourceId }) => ({
        url: `/v2/organizations/${organizationId}/resources/${organizationResourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationResource', id: organizationId },
      ],
    }),
    createOrganizationResource: builder.mutation<
      OrganizationEngagementResource,
      CreateOrganizationResourceArgs
    >({
      query: ({ organizationId, body }) => ({
        url: `/v2/organizations/${organizationId}/resources`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: { data: OrganizationEngagementResource }) => response.data,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationResource', id: organizationId },
      ],
    }),
    updateOrganizationResource: builder.mutation<
      OrganizationEngagementResource,
      UpdateOrganizationResourceArgs
    >({
      query: ({ organizationId, organizationResourceId, body }) => ({
        url: `/v2/organizations/${organizationId}/resources/${organizationResourceId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { data: OrganizationEngagementResource }) => response.data,
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: 'OrganizationResource', id: organizationId },
      ],
    }),
  }),
})

export const { resetApiState } = scribeApi.util

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useListOrganizationsQuery,
  useGetAllOrganizationsQuery,
  useLazyListOrganizationsQuery,
  useLegacyGetOrganizationQuery,
  useGetOrganizationQuery,
  useAddOrganizationMutation,
  useAddOrganizationAddressMutation,
  useUpdateOrganizationMutation,
  useUpdateOrganizationAddressMutation,
  useUpdateOrganizationContentTagsMutation,
  useLegacyGetOrganizationPlansQuery,
  useLazyLegacyGetOrganizationPlansQuery,
  useGetOrganizationPlansQuery,
  useLazyGetOrganizationPlansQuery,
  useGetOrganizationPlanQuery,
  useGetFeaturesQuery,
  useAddOrganizationPlanMutation,
  useUpdateOrganizationPlanMutation,
  useRemoveOrganizationPlanMutation,
  useGetEligibilityRecordQuery,
  useGetEligibilityRecordsQuery,
  useLazyGetEligibilityRecordsQuery,
  useLazyListEligibilityRecordsQuery,
  useAddActionRequestMutation,
  useSubmitEligibilityDirectiveMutation,
  useGetUserQuery,
  useGetUsersQuery,
  useGetUserFamilyQuery,
  useAddAdultFamilyMemberMutation,
  useAddChildFamilyMemberMutation,
  useRemoveFamilyMemberMutation,
  useUpdateOrganizationReportingColumnsMutation,
  useAddExistingFamilyMemberMutation,
  useLazyGetAllEligiblePeriodsQuery,
  useUpdatePlanMutation,
  useArchiveOrganizationPlanMutation,
  useAddAdminMutation,
  useDeleteAdminMutation,
  useGetAdminAreasQuery,
  useGetCoBrandsQuery,
  useGetBrandsQuery,
  useGetUserChargesQuery,
  useGetUserEligibilityRecordsQuery,
  useEnrolUserToEligibilityRecordMutation,
  useUnEnrolUserToEligibilityRecordMutation,
  useSendEmailVerificationMutation,
  useResendWelcomeEmailMutation,
  useResendMemberInvitationEmailMutation,
  useGetEligibilityTagsQuery,
  useAddEligibilityTagMutation,
  useUpdateEligibilityTagMutation,
  useLinkEligibilityTagToRecordMutation,
  useUnlinkEligibilityTagFromRecordMutation,
  useGetOrganizationResourcesQuery,
  useDeleteOrganizationResourceMutation,
  useCreateOrganizationResourceMutation,
  useUpdateOrganizationResourceMutation,
} = scribeApi
